<template>
  <ValidationObserver ref="conversationMessage">
    <div class="conversation-message mt-5">
      <div class="d-flex">
        <div class="d-flex align-center flex-grow-1 conversation-message__row">
          <span class="min-width-content"> Related to: </span>
          <div :class="{ 'flex-column': isMobile }" class="d-flex full-width">
            <Select
              v-model="messageDetails.relatedToType"
              @change="onEntityTypeChange"
              :items="entityTypeOptions"
              placeholder="Entity Type"
              class="underline"
              autocomplete
              label=""
              fullWidth
              dense
            />
            <ValidationProvider
              v-slot="{ errors }"
              rules="required"
              class="d-flex full-width no-border flex-grow-1"
            >
              <Select
                :error-messages="getErrors(errors)"
                v-model="messageDetails.relatedToId"
                @change="onEntityChange"
                :items="entityItems"
                :disabled="isLoadingEntityItems || isNotApplicable"
                :loading="isLoadingEntityItems"
                :placeholder="relatedToTypePlaceholder"
                item-text="title"
                item-value="id"
                class="underline"
                autocomplete
                label=""
                fullWidth
                dense
              />
            </ValidationProvider>
          </div>
        </div>
      </div>
      <div class="d-flex align-center conversation-message__row">
        <span class="min-width-content"> Template: </span>
        <div class="d-flex full-width no-border">
          <Select
            v-model="selectedTemplate"
            :items="messageTemplates"
            @change="onMessageTemplateChange"
            item-text="subject"
            :loading="isFetchingMessageTemplates"
            :disabled="isFetchingMessageTemplates || isLoading"
            placeholder="Select Template"
            class="underline"
            autocomplete
            label=""
            fullWidth
            dense
          />
        </div>
      </div>
      <div class="d-flex align-center conversation-message__row">
        <span class="min-width-content"> To: </span>
        <ValidationProvider
          v-slot="{ errors }"
          rules="required"
          class="full-width d-flex no-border"
        >
          <Select
            :error-messages="getErrors(errors)"
            multiple
            v-model="messageDetails.candidates"
            :items="formattedCandidates"
            :loading="isLoading || isLoadingCandidates"
            :placeholder="
              `Select ${isRelatedToReference ? 'Referee' : 'Candidate'}`
            "
            item-text="fullName"
            class="underline"
            autocomplete
            fullWidth
            dense
            :disabled="isLoading || isLoadingCandidates"
          >
            <template v-slot:selection="{ data }">
              <v-chip class="selected-worker-chip">
                <v-icon>mdi-account</v-icon>
                {{ data.item.fullName }}
              </v-chip>
            </template>
          </Select>
        </ValidationProvider>
      </div>
      <div class="d-flex align-center conversation-message__row" v-if="isEmail">
        <span class="min-width-content"> Subject: </span>
        <ValidationProvider
          v-slot="{ errors }"
          rules="required"
          class="full-width d-flex no-border"
        >
          <v-text-field
            aria-label="Subject"
            :error-messages="getErrors(errors)"
            placeholder="Enter Subject"
            class="underline"
            v-model="messageDetails.subject"
          />
        </ValidationProvider>
      </div>
      <quill-editor
        :key="editorId"
        v-show="isEmail"
        ref="quillEditor"
        v-model="messageDetails.bodyEmail"
        :options="editorOptions"
        @ready="adjustVariablesTextContent()"
      />
      <ValidationProvider
        v-if="!isEmail"
        v-slot="{ errors }"
        rules="required"
        class="full-width d-flex"
      >
        <v-textarea
          :error-messages="getErrors(errors)"
          height="200"
          rounded
          v-model="messageDetails.bodyText"
        />
      </ValidationProvider>
      <FileUpload
        v-if="isEmail"
        class="mt-5"
        @unselectFile="unselectFile"
        @selectFile="selectFile"
        :files="files"
        disableAutoProceed
        customActions
        isMultiple
      />
      <BaseFooter>
        <Permissioned
          :vertical="isMobile"
          section="conversations"
          class="actions-bar"
        >
          <SecondaryButton
            permission="create"
            @click.native="handleSubmit"
            color="primary"
            :loading="isCreatingConversation"
          >
            <div class="d-flex justify-center">
              <inline-svg :src="require('@/assets/svg/send_white.svg')" />
            </div>
            Send {{ format | capitalize }}
          </SecondaryButton>
        </Permissioned>
      </BaseFooter>
    </div>
  </ValidationObserver>
</template>

<script>
import Permissioned from "@/components/common/Permissioned";
import BaseFooter from "@/components/common/BaseFooter";
import Select from "@/components/common/Select";
import FileUpload from "@/components/common/FileUpload";
import { DEFAULT_TOOLBAR_OPTIONS } from "@/plugins/v-quill-editor";
import SecondaryButton from "@/components/common/Button/SecondaryButton";
import { getFilesFromUrls, convertBlobFileToBase64 } from "@/utils/files";
import {
  BOOKINGS_NAMESPACE,
  FETCH_BASIC_BOOKINGS,
  FETCH_CURRENT_BOOKING_CANDIDATES
} from "@/store/modules/bookings/actions";
import { createNamespacedHelpers } from "vuex";
import {
  GET_BOOKINGS,
  IS_LOADING_BOOKINGS
} from "@/store/modules/bookings/getters";
import {
  FETCH_MESSAGE_TEMPLATES,
  CREATE_CONVERSATION,
  ADD_CONVERSATION_MESSAGE_FILE
} from "@/store/modules/conversations/actions";
import { CONVERSATIONS_NAMESPACE } from "@/store/modules/conversations";
import { REFERENCES_NAMESPACE } from "@/store/modules/references";
import { GET_ALL_WORKERS } from "@/store/modules/workers/action-types";
import { WORKERS_NAMESPACE } from "@/store/modules/workers";
import { copyState } from "@/utils/helpers";
import InlineSvg from "vue-inline-svg";
import {
  map,
  size,
  filter,
  includes,
  toLower,
  remove,
  find,
  first,
  some,
  get
} from "lodash";
import { getFullName, getCandidateName } from "@/utils/users";
import { addSuccessMessage } from "@/services/utils";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { OFFERS_NAMESPACE } from "@/store/modules/offers";
import { FETCH_OFFERS } from "@/store/modules/offers/actions";
import { FETCH_REFERENCES } from "@/store/modules/references/actions";
import { convertHtmlToText } from "@/views/conversations/modules/utils";
import {
  BASIC_VARIABLES_TO_INSERT,
  getVariablesToInsertByEntityType,
  getConvertedEmailBody,
  insertVariable,
  adjustVariablesTextContent,
  adjustAlreadyInsertedVariables
} from "@/utils/conversations";
import generateRandomId from "uuid/v4";
import {
  PROFESSIONAL_REFERENCE_TYPE_NAME,
  OFFER_CATEGORY_VALUE,
  NOT_APPLICABLE,
  OFFER,
  ROLE,
  REFERENCE
} from "@/constants/conversations";

const {
  mapActions: mapBookingActions,
  mapGetters: mapBookingGetters
} = createNamespacedHelpers(BOOKINGS_NAMESPACE);

const {
  mapActions: mapConversationActions,
  mapState: mapConversationState
} = createNamespacedHelpers(CONVERSATIONS_NAMESPACE);

const {
  mapActions: mapOfferActions,
  mapState: mapOfferState
} = createNamespacedHelpers(OFFERS_NAMESPACE);

const {
  mapActions: mapReferenceActions,
  mapState: mapReferenceState
} = createNamespacedHelpers(REFERENCES_NAMESPACE);

const {
  mapActions: mapWorkerActions,
  mapState: mapWorkerState
} = createNamespacedHelpers(WORKERS_NAMESPACE);

export default {
  name: "ConversationMessage",
  components: {
    BaseFooter,
    Permissioned,
    Select,
    FileUpload,
    SecondaryButton,
    ValidationObserver,
    ValidationProvider,
    InlineSvg
  },
  props: {
    format: {
      type: String,
      required: true
    },
    selectedBookingId: String,
    selectedOfferId: String,
    selectedCandidatesIds: Array
  },
  async created() {
    await this.fetchBookings(this.requestParams);
    if (this.selectedBookingId) {
      this.adjustFormForOuterSelectedBooking();
    } else if (this.selectedCandidatesIds) {
      this.adjustNotApplicableEntity();
    }
    if (this.selectedOfferId) {
      this.adjustFormForOuterSelectedOffer();
    }
  },
  data() {
    return {
      editorId: generateRandomId(),
      showValidationErrors: false,
      messageDetails: {
        bodyText: "",
        bodyEmail: "",
        candidates: [],
        subject: "",
        bookingId: null,
        relatedToType: ROLE,
        relatedToId: "",
        recipientType: "user"
      },
      selectedTemplate: null,
      files: [],
      referees: [],
      offerRecipient: null,
      isLoading: false,
      isCreatingConversation: false,
      outerSelectedCandidatesIds: this.selectedCandidatesIds,
      entityTypeOptions: [ROLE, OFFER, REFERENCE, NOT_APPLICABLE],
      isLoadingCandidates: false,
      allCandidates: [],
      uppyInstance: null,
      attachments: []
    };
  },
  computed: {
    ...mapOfferState(["offers", "isFetchingOffers"]),
    ...mapReferenceState(["references", "isFetchingReferences"]),
    ...mapConversationState(
      copyState(["messageTemplates", "isFetchingMessageTemplates"])
    ),
    ...mapWorkerState(["workers"]),
    ...mapBookingGetters({
      bookings: GET_BOOKINGS,
      isLoadingBookings: IS_LOADING_BOOKINGS
    }),
    isUserRecpient() {
      return this.isRelatedToReference || this.isNotApplicable;
    },
    relatedToTypePlaceholder() {
      const prefix = this.isNotApplicable ? "" : "Select ";
      return `${prefix}${this.messageDetails.relatedToType}`;
    },
    formattedReferences() {
      return map(this.references, reference => ({
        id: reference.id,
        title: this.getReferenceTitle(reference)
      }));
    },
    formattedRelatedToType() {
      if (this.isNotApplicable) {
        return null;
      }
      const relatedToType = toLower(this.messageDetails.relatedToType);

      return relatedToType === "role" ? "booking" : relatedToType;
    },
    isRelatedToReference() {
      return toLower(this.messageDetails.relatedToType) === "reference";
    },
    isRelatedToOffer() {
      return toLower(this.messageDetails.relatedToType) === "offer";
    },
    isRelatedToRole() {
      return toLower(this.messageDetails.relatedToType) === "role";
    },
    isNotApplicable() {
      return this.messageDetails.relatedToType === NOT_APPLICABLE;
    },
    formattedOffers() {
      return map(
        filter(this.offers, ({ application }) => application),
        ({ application, id }) => ({
          id,
          title: `${getFullName(application.user)} -> ${
            application.booking.title
          }`
        })
      );
    },
    isEmail() {
      return this.format === "email";
    },
    formattedCandidates() {
      let candidates = this.allCandidates;
      if (this.isRelatedToReference) {
        candidates = this.referees;
      }
      if (this.isRelatedToOffer) {
        candidates = this.offerRecipient ? [this.offerRecipient] : [];
      }
      if (this.isNotApplicable) {
        return map(this.workers, worker => ({
          ...worker,
          fullName: getFullName(worker)
        }));
      }
      return map(candidates, item => ({
        ...item,
        fullName: item.user ? getCandidateName(item) : getFullName(item)
      }));
    },
    editorOptions() {
      const showInsertVariable = !some(
        this.messageDetails.candidates,
        candidate =>
          get(candidate, "user.firstName") === null &&
          get(candidate, "user.lastName") === null
      );
      return {
        modules: {
          toolbar: {
            container: [
              ...DEFAULT_TOOLBAR_OPTIONS,
              [
                {
                  ...(showInsertVariable && {
                    "insert-variable": [
                      ...BASIC_VARIABLES_TO_INSERT,
                      ...getVariablesToInsertByEntityType(
                        toLower(this.messageDetails.relatedToType)
                      )
                    ]
                  })
                }
              ]
            ],
            handlers: {
              "insert-variable": val =>
                insertVariable(val, this.$refs.quillEditor.quill)
            }
          }
        }
      };
    },
    conversationConvertedBody() {
      return this.isEmail
        ? getConvertedEmailBody(this.messageDetails.bodyEmail)
        : this.messageDetails.bodyText;
    },
    entityItems() {
      switch (toLower(this.messageDetails.relatedToType)) {
        case "role":
          return map(this.bookings, booking => ({
            ...booking,
            title: `R-${booking.id} ${booking.title} `
          }));
        case "offer":
          return this.formattedOffers;
        case "reference":
          return this.formattedReferences;
        default:
          return [];
      }
    },
    isLoadingEntityItems() {
      return (
        this.isLoadingBookings ||
        this.isFetchingOffers ||
        this.isFetchingReferences
      );
    }
  },
  methods: {
    ...mapOfferActions({
      fetchOffers: FETCH_OFFERS
    }),
    ...mapReferenceActions({
      fetchReferences: FETCH_REFERENCES
    }),
    ...mapBookingActions({
      fetchBookings: FETCH_BASIC_BOOKINGS,
      fetchCurrentBookingApplications: FETCH_CURRENT_BOOKING_CANDIDATES
    }),
    ...mapConversationActions({
      fetchMessageTemplates: FETCH_MESSAGE_TEMPLATES,
      createConversation: CREATE_CONVERSATION,
      addConversationMessageFile: ADD_CONVERSATION_MESSAGE_FILE
    }),
    ...mapWorkerActions({
      fetchWorkers: GET_ALL_WORKERS
    }),
    async uploadFiles(messageId) {
      const requests = map(this.files, file => {
        const formData = new FormData();
        formData.append("file", file.data);
        return this.addConversationMessageFile({
          id: messageId,
          data: formData
        });
      });
      return Promise.all(requests);
    },
    async adjustFormForOuterSelectedBooking() {
      this.messageDetails.relatedToId = this.selectedBookingId;
      await this.onEntityChange(this.selectedBookingId);
      if (size(this.outerSelectedCandidatesIds)) {
        this.adjustAlreadySelectedCandidates();
      }
    },
    async adjustNotApplicableEntity() {
      this.messageDetails.relatedToId = NOT_APPLICABLE;
      this.messageDetails.relatedToType = NOT_APPLICABLE;
      this.isLoading = true;
      await this.loadWorkers();
      this.isLoading = false;
      if (size(this.outerSelectedCandidatesIds)) {
        this.adjustAlreadySelectedCandidates();
      }
    },
    async loadWorkers() {
      this.isLoading = true;
      await this.fetchWorkers({
        filter: {
          account_type: "worker"
        }
      });
      this.isLoading = false;
    },
    async adjustFormForOuterSelectedOffer() {
      this.messageDetails.relatedToType = OFFER;
      await this.onEntityTypeChange("offer");
      this.messageDetails.relatedToId = this.selectedOfferId;
      await this.onEntityChange(this.selectedOfferId);
      this.outerSelectedCandidatesIds = [
        find(this.offers, { id: this.selectedOfferId }).application.id
      ];
      this.adjustAlreadySelectedCandidates();
      this.selectedTemplate = first(
        filter(
          this.messageTemplates,
          ({ category }) => category === OFFER_CATEGORY_VALUE
        )
      );
      if (this.selectedTemplate) {
        this.onMessageTemplateChange();
      }
    },
    getBookingIdsForCurrentEntity(entityId) {
      switch (toLower(this.messageDetails.relatedToType)) {
        case "role":
          return [entityId];
        case "offer":
          return [find(this.offers, { id: entityId }).application.booking.id];
        case "reference":
          return map(
            find(this.references, { id: entityId }).applications,
            ({ booking }) => booking.id
          );
      }
    },
    getReferenceTitle({ user, referee, referenceTypeName, experience }) {
      const userFullName = getFullName(user);
      if (referenceTypeName === PROFESSIONAL_REFERENCE_TYPE_NAME) {
        return `${userFullName} - ${experience.organisation}`;
      }
      return `${userFullName} (${getFullName(referee)} as Referee)`;
    },
    getErrors(errors) {
      return this.showValidationErrors ? errors : [];
    },
    async onEntityChange(entityId) {
      const bookingIdsForCurrentEntity = this.getBookingIdsForCurrentEntity(
        entityId
      );
      this.fetchMessageTemplates(bookingIdsForCurrentEntity);
      if (this.isRelatedToReference) {
        this.messageDetails.recipientType = "referee";
        const reference = find(this.references, { id: entityId });
        this.referees = [reference.referee];
      } else {
        this.messageDetails.recipientType = "user";
      }
      if (this.isRelatedToOffer) {
        const offer = find(this.offers, { id: entityId });
        this.offerRecipient = offer.application;
      }
      if (this.isRelatedToRole) {
        await this.fetchAllCandidates(bookingIdsForCurrentEntity);
      }
    },
    async onEntityTypeChange(val) {
      this.editorId = generateRandomId();
      this.messageDetails.candidates = [];
      this.selectedTemplate = null;
      switch (toLower(val)) {
        case "role":
          return this.fetchBookings();
        case "offer":
          return this.fetchOffers();
        case "n/a":
          this.messageDetails.relatedToId = NOT_APPLICABLE;
          return this.loadWorkers();
        case "reference":
          return this.fetchReferences({
            filter: {
              ...(this.isEmail && { has_referee_with_email: true }),
              ...(!this.isEmail && { has_referee_with_phone_number: true })
            }
          });
      }
    },
    async fetchAllCandidates(bookingIds) {
      this.isLoadingCandidates = true;
      await Promise.all(
        map(bookingIds, async bookingId => {
          const { data } = await this.fetchCurrentBookingApplications({
            bookingId
          });
          this.allCandidates = [...this.allCandidates, ...data];
        })
      );
      this.isLoadingCandidates = false;
    },
    adjustAlreadySelectedCandidates() {
      this.messageDetails.candidates = filter(
        this.formattedCandidates,
        ({ id }) => includes(this.outerSelectedCandidatesIds, id)
      );
      this.outerSelectedCandidatesIds = [];
    },
    async onMessageTemplateChange() {
      const { body, subject, attachments } = this.selectedTemplate;
      this.messageDetails.bodyText = convertHtmlToText(body);
      this.messageDetails.bodyEmail = body;
      this.messageDetails.subject = subject;

      // adjust template inserted variables, after a delay
      setTimeout(() => {
        adjustAlreadyInsertedVariables();
      }, 100);
      this.files = await getFilesFromUrls(attachments);
    },
    selectFile({ file }) {
      this.files.push(file);
    },
    unselectFile({ uppyInstance, file }) {
      this.removeFile(file);
      uppyInstance.reset();
    },
    removeFile(fileToBeRemoved) {
      remove(this.files, file => file.id === fileToBeRemoved.id);
    },
    async handleSubmit() {
      this.showValidationErrors = true;
      const isValid = await this.$refs.conversationMessage.validate();
      if (isValid) {
        this.onCreateConversation();
      }
    },
    async getAttachments() {
      this.attachments = [];
      await Promise.all(
        map(this.files, async file => {
          const data = await convertBlobFileToBase64(file.data);
          this.attachments.push({ data, fileName: file.name });
        })
      );
    },
    async onCreateConversation() {
      this.isCreatingConversation = true;
      const { subject, relatedToId, recipientType } = this.messageDetails;
      await this.getAttachments();
      const requests = map(this.messageDetails.candidates, candidate =>
        this.createConversation({
          body: this.conversationConvertedBody,
          ...(this.isEmail && { subject }),
          conversationFormat: this.isEmail ? this.format : "sms",
          relatedToType: this.formattedRelatedToType,
          relatedToId: this.isNotApplicable ? null : relatedToId,
          recipientType,
          recipientId: this.isUserRecpient ? candidate.id : candidate.user.id,
          attachments: this.attachments
        })
      );
      const conversations = await Promise.all(requests);
      const isSuccess = size(conversations.filter(Boolean));

      if (isSuccess) {
        this.onSuccess(conversations);
      }
      this.isCreatingConversation = false;
      this.showValidationErrors = false;
    },
    async createConversationMessageFiles(conversations) {
      const requests = map(conversations, conversation =>
        this.uploadFiles(conversation.data.lastMessage.id)
      );
      return Promise.all(requests);
    },
    onSuccess(conversations) {
      addSuccessMessage();
      if (size(conversations) === 1) {
        this.$router.push({
          name: "conversationDetails",
          params: { conversationId: first(conversations).data.id }
        });
      } else {
        this.$router.push("/conversations");
      }
    },
    first,
    adjustVariablesTextContent
  },
  watch: {
    editorOptions() {
      this.editorId = generateRandomId();
      this.onMessageTemplateChange();
    }
  }
};
</script>

<style lang="scss">
.conversation-message {
  .no-border .error--text .v-input__slot {
    border: none !important;
    box-shadow: none;
  }
  .select.underline,
  .v-input.underline {
    .v-input__slot {
      border-radius: 0;
      border: none;
      border-bottom: 1px solid $secondary-line;
    }
  }
  .select.underline {
    flex-grow: 1;
    margin-top: 0px;

    .v-input--is-focused .v-input__slot {
      box-shadow: none;
      border: none;
    }
  }
  .v-input.underline {
    flex-grow: 1;
    padding: 0 !important;

    &.v-input--is-focused .v-input__slot {
      border-bottom-color: $primary;
      box-shadow: none;
    }
  }

  .quill-editor {
    min-height: 500px;

    .ql-container.ql-snow {
      height: 100%;
      max-height: 450px;
      overflow-y: auto;
    }
  }
}
</style>
